import { GRAY_600, GRAY_900 } from 'constants/styling/theme'
import { Typography, inputBaseClasses } from '@mui/material'

import { Language } from 'translations/Language'
import LanguageIcon from '@mui/icons-material/Language'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { applyLanguageChange } from 'utils/localization/applyLanguageChange'
import { coerceLanguage } from 'utils/localization'
import i18n from 'translations/i18n'
import { useAuth0 } from 'utils/auth'
import { useChangeLocale } from 'dataQueries'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * @component LanguageDropdown displayed TopBar
 * @example <LanguageDropdown />
 */
export const LanguageDropdown: React.FC = () => {
  const { t } = useTranslation('topbar')
  const { isAuthenticated } = useAuth0()
  const { baseUserData } = useUserData()
  const changeLocale = useChangeLocale()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedLanguage = useMemo(() => coerceLanguage(i18n.language), [i18n.language])

  return (
    <MUIDropdown
      button={(isOpen, action) => (
        <MUIDropdownInput
          sx={{
            borderRadius: '4rem', // Rounded corners
            [`.${inputBaseClasses.root}`]: {
              flex: 1,
              width: '2rem', // Adjusted to fit the two letters of the language name
              marginRight: '-1.2rem' // Adjusted to make the dropdown more compact and rounded
            }
          }}
          value={selectedLanguage}
          onClick={action}
          customInputContent={
            <LanguageIcon sx={{ color: GRAY_600, fontSize: '1.8rem', alignSelf: 'center' }} />
          }
        />
      )}>
      {Object.values(Language)?.map(lang => {
        return (
          <MUIDropdownItem
            key={lang}
            onClick={() => {
              applyLanguageChange(lang)
              if (!isAuthenticated || !baseUserData?.email) return
              changeLocale.mutate({ email: baseUserData.email, language: lang })
            }}
          >
            <Typography variant="text-md" fontWeight={400} color={GRAY_900}>
              {t(`language:${lang.toUpperCase()}`, lang)}
            </Typography>
          </MUIDropdownItem>
        )
      })}
    </MUIDropdown>
  )
}
